import {useEffect, useState} from 'react';
import {Box, Dialog, DialogContent, Grid, IconButton, Typography} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {TAPES_ARRAY} from '../../utils/constants';
import {useAuth} from "../AuthProvider/AuthProvider";
import {StyledButtonContained} from "../../utils/uiUtils";
import {fetchTapeDetails, fetchTapeImageDetails} from "../../utils/dashboardCalls";

const formatBytes = (bytes, decimals = 0) => {
    if (!+bytes) return '0'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}
// How many photographs the popup shows side by side before it needs arrows.
const PHOTOGRAPHS_IN_VIEW = 3;

export const TapeDetails = ({open, closePopup, tapeId, cid}) => {
    const [tapeData, setTapeData] = useState("")
    const [photographs, setPhotographs] = useState([])
    const [photographIndex, setPhotographIndex] = useState(0)
    const {features, isAdmin, user} = useAuth();

    // Use provided cid (from parent with customer context) or derive from auth
    const effectiveCid = cid ?? (isAdmin ? 0 : user?.arkbridge_customer_id);

    useEffect(() => {
        if (features.length > 0 && tapeId) {
            // Cleared first, so that a tape opened after one that had a photograph does not appear to
            // have that photograph until its own answer arrives — or, if it has none, for as long as
            // the popup stays open.
            setTapeData("");
            setPhotographs([]);
            setPhotographIndex(0);
            getTapeDetails();
            getTapeImageDetails(tapeId);
        }
    }, [features, tapeId])

    const getTapeImage = (media_type) => {
        let _data = TAPES_ARRAY.filter(e => e.fileName.includes(media_type));
        return _data.length && _data[0].filePath
    }

    /*
     * Both requests catch, because both used to reject into nothing.
     *
     * An uncaught rejection in development is the full-screen 'Uncaught runtime errors' overlay, which is
     * what opening this popup produced: the photograph endpoint answers 400 on a local deployment, where
     * it is not registered, and one optional picture failing took the whole page down. In production the
     * same thing happens to any tape whose image row cannot be read. Neither is a reason for the reader
     * to lose the page — the details they clicked for arrive from the other request, and the photograph
     * either shows or says it is not there.
     */
    const getTapeDetails = () => {
        fetchTapeDetails(effectiveCid, tapeId)
            .then(data => { if (data) setTapeData(data); })
            .catch(error => console.error('Could not load the details of this tape:', error));
    }

    /*
     * The photograph, and the one failure on this page that is not worth a word.
     *
     * Caught to keep the rejection from reaching the dev overlay, and then deliberately not logged. A tape
     * with no photograph is ordinary, and `man_item_image_get` is not registered on a local deployment at
     * all, so it answers 400 for every tape opened: logging it printed a stack per open, on top of the same
     * stack from dashboardCalls and the browser's own note about the 400, and a console full of expected
     * failures is one nobody reads a real one out of. The absence is already on screen where the picture
     * would be, which is the whole of what a reader needs.
     *
     * Clearing the state rather than taking no action at all: the effect blanks it before asking, so this is
     * only belt and braces, but it is what says 'no photograph' rather than 'the previous tape's photograph'
     * should the reset above ever be moved.
     */
    const getTapeImageDetails = () => {
        fetchTapeImageDetails(effectiveCid, tapeId)
            .then(data => setPhotographs(Array.isArray(data) ? data : []))
            .catch(() => setPhotographs([]));
    }

    // A strip of up to PHOTOGRAPHS_IN_VIEW side by side, turned a page at a time: 1–3, then 4–6, the last page
    // holding whatever is left. It used to slide one photograph along (1–3, then 2–4), which showed two of the
    // three again on every click. The arrows stop at either end, so the count always describes what is shown.
    const lastFirstShown = Math.max(Math.floor((photographs.length - 1) / PHOTOGRAPHS_IN_VIEW) * PHOTOGRAPHS_IN_VIEW, 0);
    const shown = photographs.slice(photographIndex, photographIndex + PHOTOGRAPHS_IN_VIEW);
    const slidePhotographs = (step) => {
        setPhotographIndex(index => Math.min(Math.max(index + step * PHOTOGRAPHS_IN_VIEW, 0), lastFirstShown));
    }
    const photographLabel = (position) => `Photograph of tape ${tapeData?.item_volser || ''}`.trim()
        + (photographs.length > 1 ? ` (${position} of ${photographs.length})` : '');
    const handleClose = () => {
        closePopup();
    };

    return (<>
            <Dialog open={open} fullWidth={true}
                    sx={{
                        '& .MuiDialog-paper': {
                            borderRadius: '8px',
                            minWidth: '1100px'
                        },
                    }}>
                <DialogContent sx={{p: 4, backgroundColor: '#3A3E3F', color: '#FFFFFF', paddingRight: '0px'}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center'}}>
                        {/* <Typography sx={{ color: '#FFC540', fontSize: 18 }}>Tape Details</Typography> */}

                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} style={{paddingTop: '0px'}}>
                            {/*
                              * Rendered only when there is a picture to render. An <img> with no
                              * src asks the browser for the page itself, which comes back as HTML
                              * and draws as a broken-image icon where the tape should be — a
                              * fault where the honest answer is that this tape has no photograph.
                              */}
                            {shown.length > 0
                                ? <Box>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, pr: 4}}>
                                        {/* Only when there are more than fit: arrows beside a strip that already shows everything would suggest photographs that are not there. */}
                                        {photographs.length > PHOTOGRAPHS_IN_VIEW &&
                                            <IconButton aria-label='Previous photographs' onClick={() => slidePhotographs(-1)}
                                                        disabled={photographIndex === 0} sx={{color: '#FFC540'}}>
                                                <ChevronLeftIcon/>
                                            </IconButton>}
                                        {shown.map((image, offset) =>
                                            // Opens the full-size original: the strip scales each one down to fit three across.
                                            <a key={image.presigned_url} href={image.presigned_url} target='_blank' rel='noopener noreferrer'
                                               style={{flex: `0 1 calc(100% / ${PHOTOGRAPHS_IN_VIEW})`, display: 'flex', justifyContent: 'center'}}>
                                                <img style={{height: '320px', maxWidth: '100%', objectFit: 'contain'}}
                                                     src={image.presigned_url}
                                                     alt={photographLabel(photographIndex + offset + 1)}/>
                                            </a>)}
                                        {photographs.length > PHOTOGRAPHS_IN_VIEW &&
                                            <IconButton aria-label='Next photographs' onClick={() => slidePhotographs(1)}
                                                        disabled={photographIndex === lastFirstShown} sx={{color: '#FFC540'}}>
                                                <ChevronRightIcon/>
                                            </IconButton>}
                                    </Box>
                                    {photographs.length > PHOTOGRAPHS_IN_VIEW &&
                                        <Typography align='center' sx={{pr: 4}}>
                                            {shown.length > 1
                                                ? `${photographIndex + 1}–${photographIndex + shown.length}`
                                                : photographIndex + 1} of {photographs.length}
                                        </Typography>}
                                </Box>
                                : <Typography sx={{height: '320px', display: 'flex', alignItems: 'center', color: 'rgba(255, 255, 255, 0.60)'}}>
                                    No photograph of this tape
                                </Typography>}
                        </Grid>
                        <Grid container spacing={2}>

                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Bar Code </Typography>
                                        <Typography>{tapeData?.item_volser}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'> Alt Bar Code </Typography>
                                        <Typography>{tapeData?.other_volsers?.join(", ")}</Typography>
                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>TapeArk Bar Code </Typography>
                                        <Typography>{tapeData?.item_barcode}</Typography>
                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Case Bar Code </Typography>
                                        <Typography>{tapeData?.item_volser}</Typography>
                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>TapeArk Case Bar Code </Typography>
                                        <Typography>{tapeData?.box_barcode}</Typography>
                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Format</Typography>
                                        <Typography>{tapeData?.tape_format}</Typography>
                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Site</Typography>
                                        <Typography>{tapeData?.site}</Typography>
                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Media Type </Typography>
                                        <Typography>{tapeData?.media_type}</Typography>

                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Manufacturer</Typography>
                                        <Typography>{tapeData?.brand}</Typography>

                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Brand</Typography>
                                        <Typography>{tapeData?.brand}</Typography>
                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Tape Ark ID </Typography>
                                        <Typography>{tapeData?.item_id}</Typography>

                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Catalogued Date </Typography>
                                        <Typography>{tapeData?.created}</Typography>

                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Retention Date </Typography>
                                        <Typography>{tapeData?.retention_date}</Typography>

                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Catalogued Used</Typography>
                                        <Typography></Typography>
                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Maximum Capacity</Typography>
                                        {/* Capacity is returned in MB, so needs to be multiplied by 1MB to get raw bytes. */}
                                        <Typography>{formatBytes(tapeData?.native_capacity_mb * 1024 * 1024)}</Typography>
                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                                    <Grid item style={{minWidth: '200px'}}>
                                        <Typography align='left'>Operator Notes</Typography>
                                        <Typography>{tapeData?.operator_notes}</Typography>

                                    </Grid>

                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                    </Grid>
                                    <Grid item xs={6}>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 2}}>
                        <StyledButtonContained variant='contained'
                                               onClick={handleClose}
                                               sx={{
                                                   width: '180px',
                                                   height: '60px',
                                                   marginLeft: 'auto',
                                                   marginRight: "50px"
                                               }}>
                            Close
                        </StyledButtonContained>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    )
}
